import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMasters } from "./masterData";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

// 현재 라우트가 특정 Opportunity 컨텍스트(flow-builder ?projectId= / 프로젝트 상세 /crm/projects/:id)면 그 projectId 반환.
// 사이드바 문서 자동화 링크에 project_id 를 붙여 문서 페이지가 Opportunity 를 자동 매핑하도록 한다.
function getActiveProjectId(pathname: string, search: string): string | null {
  if (pathname.startsWith("/crm/flow-builder")) {
    return new URLSearchParams(search).get("projectId");
  }
  const m = pathname.match(/^\/crm\/(?:opportunities|projects)\/([0-9a-fA-F-]{8,})$/);
  return m ? m[1] : null;
}
import {
  FolderKanban,
  Braces,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronDown,
  FileText,
  Files,
  ClipboardList,
  FlaskConical,
  FolderOpen,
  GitBranch,
  Handshake,
  HardDrive,
  HelpCircle,
  Inbox,
  CalendarRange,
  GanttChartSquare,
  KanbanSquare,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Mail,
  Map,
  Megaphone,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,

  ShieldCheck,
  SlidersHorizontal,
  Target,
  Truck,
  Dot,} from "lucide-react";
import ComingSoonModal from "./components/ComingSoonModal";
import FeatureStatusBadge, { FeatureStatus } from "./components/FeatureStatusBadge";
import HelpAssistantDrawer from "./components/HelpAssistantDrawer";
import { useLanguage } from "../../components/ui/LanguageProvider";
import type { Language } from "../../components/ui/LanguageProvider";
import type { DocumentMenuEntry } from "../automation/automationMenuConfig";
import { documentKeyForPath, documentMenuRoleKey } from "../automation/common/documentRegistry";
import { useDocumentCatalog } from "../../components/auth/DocumentCatalogContext";
import type { DocumentMenuItem } from "../automation/automationMenuConfig";

/**
 * 문서 메뉴 링크 경로. 원래 규칙은 `/crm/document-automation/${key}` 이고 path 필드는
 * 보지 않았다. 그 규칙 밖에 있는 화면(계약서 = /crm/contracts)만 absolutePath 를 준다.
 * ★ 기존 항목은 absolutePath 가 없어 동작이 그대로다.
 */
/** 메뉴 관리에서 지정한 아이콘(applyMenuOverrides 가 실어 준다). 없으면 null → 기본 문서 아이콘. */
function docIcon(entry: unknown): React.ReactNode {
  return (entry as { icon?: React.ReactNode } | null)?.icon ?? null;
}

function docEntryPath(item: DocumentMenuItem): string {
  return item.absolutePath || `/crm/document-automation/${item.key}`;
}
import RouteSuspense from "../../components/layout/RouteSuspense";
import { getCrmDocumentLabel, getCrmMenuLabel, getCrmSectionLabel } from "./crmLanguage";
import { applyMenuOverrides } from "../../data/menuOverrides";
import { itemsMovedAwayFrom, sectionLabelOf } from "../../data/menuPlacement";
import { resolveMenuLabel } from "../../data/menuLabels";
import { renderMenuIcon } from "../../data/menuIcons";
import { useMenuRoles } from "../../components/auth/MenuRoleContext";
import AccessDeniedNotice from "../../components/auth/AccessDeniedNotice";
import type { Role } from "../../components/auth/AuthContext";
import { ROLE_RANK, useAuth } from "../../components/auth/AuthContext";
import useIsMobile, { isMobileViewport } from "../../components/ui/useIsMobile";
import MobileApp from "../../mobile/MobileApp";
import AppContextBar, { contextActionBtnClass } from "../../components/layout/AppContextBar";
import { PageMetaProvider, pageTitleOf } from "../../components/layout/PageMetaContext";
import type { Crumb, PageMeta } from "../../components/layout/PageMetaContext";
import ProductSwitcher from "../../components/layout/ProductSwitcher";
import AccountMenu from "../../components/layout/AccountMenu";
import { DUR, EASE, moveIndicator, t as motionT } from "../../lib/motion";
import { fetchLeadInboxLeads } from "./crmLeadService";
import { fetchCompanies } from "./companies/companyService";
import { isMenuSearchShortcut, MenuSearchInput, sideItemStyle, sideSectionLabelStyle } from "../../components/layout/sidebarShared";

type SideMenuStatus = "active" | "beta" | "development" | "coming_soon";

type SideMenuItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  status?: SideMenuStatus;
  comingSoon?: boolean;
  /** 노출 최소 role. 생략 시 viewer (guest는 default-deny) — 1-b에서 DB화 예정 */
  minRole?: Role;
  /** 권한 키 — applyMenuOverrides 가 붙인다(다른 섹션·대메뉴에서 옮겨 온 메뉴는 조합 키와 다르다) */
  menuKey?: string;
};

type SideMenuSection = {
  key: string;
  label: string;
  /** 접힘(아이콘 전용) 상태에서 섹션 대표로 보여줄 1depth 아이콘 */
  icon?: React.ReactNode;
  items?: SideMenuItem[];
  /** Document automation template entries (rendered by DocEntriesRenderer) */
  docEntries?: DocumentMenuEntry[];
  /** Extra items rendered after docEntries (allows mixing both in one section) */
  extraItems?: SideMenuItem[];
  /** 노출 최소 role. 생략 시 viewer — 섹션 단위 게이트 */
  minRole?: Role;
  /** 권한 키 — applyMenuOverrides 가 붙인다(IT-CONSULTING 에서 옮겨 온 섹션은 automation:…) */
  menuKey?: string;
};

// 권한 키 — 오버라이드가 붙인 실제 키 우선. 조합 키는 옮겨 온 메뉴에서 틀린다.
const sectionMenuKeyOf = (section: SideMenuSection) => section.menuKey ?? `crm:${section.key}`;
const itemMenuKeyOf = (section: SideMenuSection, item: SideMenuItem) => item.menuKey ?? `crm:${section.key}:${item.key}`;

const MENU_SECTIONS: SideMenuSection[] = [
  {
    key: "overview",
    label: "Overview",
    icon: <LayoutGrid size={18} />,
    items: [
      { key: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={16} />, path: "/crm/dashboard", status: "active" },
      { key: "calendar", label: "Calendar", icon: <CalendarDays size={16} />, path: "/crm/calendar", status: "active" },
      // 임원 보고·확인용 Opportunity 현황(읽기 전용). 편집은 Opportunity 섹션에서.
      { key: "opportunity-overview", label: "Opportunity Overview", icon: <ClipboardList size={16} />, path: "/crm/opportunity-overview", status: "active" },
      { key: "automation-roadmap", label: "Automation Roadmap", icon: <Map size={16} />, path: "/crm/automation-roadmap", status: "active" },
    ],
  },
  {
    key: "sales",
    label: "Sales",
    icon: <Briefcase size={18} />,
    items: [
      { key: "leads", label: "Lead Inbox", icon: <Inbox size={16} />, path: "/crm/leads", status: "active" },
      { key: "mailbox", label: "Mailbox", icon: <Mail size={16} />, path: "/crm/mailbox", status: "active" },
      // COMPANY-CONTACTS 병합 — 회사(옛 Accounts)+담당자(옛 Contacts)를 한 화면·같은 양식으로.
      //   옛 두 메뉴와 주소(/crm/accounts · /crm/contacts)는 없앴다 — 구독 상세도 이 화면에 있다.
      { key: "relationships", label: "Relationships", icon: <Building2 size={16} />, path: "/crm/relationships", status: "active" },
      { key: "partners", label: "Partners", icon: <Handshake size={16} />, path: "/crm/partners", status: "active" },
      // Pipeline은 Opportunity 섹션으로 이관(영업기회 리스트 + 파이프라인 운영을 한 섹션에 통합).
      // 통합 A안 단계 3 — 파트너별 역량·평가는 파트너 상세 탭으로 이동했다.
      // 전체 관점(비교·품질·가격·등급분포)만 별도 화면으로 남긴다.
      { key: "partner-analysis", label: "Partner Analysis", icon: <SlidersHorizontal size={16} />, path: "/crm/partners/analysis", status: "active" },
      // Package(Vault) Mapping 은 파트너 상세 › 역량 · 가격 › 패키지 매핑 서브탭으로 이관(2026-09-27).
      //   패키지를 바꿀 때만 쓰는 화면이라 메뉴를 따로 둘 만큼 쓰이지 않는다. 옛 주소는 리다이렉트된다.
      // 잠재 파트너사(미등록 후보) 적합성 평가. 등록 파트너의 평가는 파트너 상세 탭에 있다.
      { key: "partner-verification", label: "Partner Verification", icon: <ShieldCheck size={16} />, path: "/crm/partner-verification", status: "active" },
    ],
  },
  {
    key: "marketing",
    label: "Marketing",
    icon: <Megaphone size={18} />,
    items: [
      { key: "marketing-dashboard", label: "Marketing Dashboard", icon: <LayoutDashboard size={16} />, path: "/crm/marketing/dashboard", status: "active" },
      { key: "event", label: "Event(참석자)", icon: <CalendarRange size={16} />, path: "/crm/marketing/events", status: "active" },
    ],
  },
  {
    key: "opportunity",
    label: "Opportunity",
    icon: <Target size={18} />,
    items: [
      { key: "pipeline", label: "영업 기회", icon: <KanbanSquare size={16} />, path: "/crm/opportunities", status: "active" },
      { key: "wbs", label: "WBS", icon: <GanttChartSquare size={16} />, path: "/crm/wbs", status: "active" },
      { key: "pipeline-settings", label: "Pipeline Settings", icon: <GitBranch size={16} />, path: "/crm/pipeline-settings", status: "active" },
    ],
  },
  {
    // 수주 이후 단계 — Opportunity(수주까지)와 다루는 구간이 다르므로 그룹을 나눈다.
    key: "project",
    label: "Project",
    icon: <FolderKanban size={18} />,
    items: [
      { key: "projects", label: "Projects", icon: <FolderKanban size={16} />, path: "/crm/projects", status: "active" },
      // 실행 파이프라인 전용 — 영업 파이프라인과 섞이면 잘못된 걸 편집하게 된다.
      { key: "project-pipeline-settings", label: "Pipeline Settings", icon: <GitBranch size={16} />, path: "/crm/project-pipeline-settings", status: "active" },
    ],
  },
  {
    // ★ STEP 4: 문서 10줄(견적서 3 · 계약서 · 인보이스 · MOU 3 · NDA · 수주품의서)을 메뉴에서 내리고
    //   "문서 템플릿" 한 줄로 합쳤다. 문서 전환은 통합 작성 화면(/crm/documents/:docKey)의 레일이 맡는다.
    //   Proposal · 3D Simulation · Site Data Analysis 는 **통합 대상이 아니다** — 별도 화면으로 이 그룹에 남는다.
    //   DOCUMENT_MENU_ITEMS 자체는 플로우빌더 문서 피커가 계속 쓰므로 그대로 살아 있다.
    key: "document-automation",
    label: "DOCUMENTS",
    icon: <Files size={18} />,
    items: [
      { key: "documents", label: "문서 템플릿", icon: <Files size={16} />, path: "/crm/documents/quote", status: "active" },
      { key: "proposal", label: "Proposal", icon: <LayoutTemplate size={16} />, path: "/crm/document-automation/proposal", status: "beta" },
    ],
    extraItems: [
      { key: "3d-simulation", label: "Digital Twin Sandbox", icon: <Braces size={16} />, path: "/crm/3d-simulation", status: "active" },
      { key: "logistics-data-lab", label: "Site Data Analysis", icon: <Truck size={16} />, path: "/crm/logistics-data-lab", status: "active" },
    ],
  },
  {
    key: "resources",
    label: "Resources",
    icon: <FolderOpen size={18} />,
    items: [
      { key: "crm-files", label: "CRM 파일", icon: <FolderOpen size={16} />, path: "/crm/files", status: "active" },
      // 회사 자료 라이브러리(COL-63) — 조회 viewer+(기본), 관리 버튼은 페이지 내 admin 게이트
      { key: "company-assets", label: "회사 자료", icon: <FolderOpen size={16} />, path: "/crm/company-assets", status: "active" },
    ],
  },
  {
    key: "template",
    label: "Template",
    icon: <LayoutTemplate size={18} />,
    minRole: "admin",
    items: [
      // Email / Slack 을 Communication Templates 하나로 합치면서 메뉴에서 내렸다가,
      // 사용자 요청으로 되살렸다(2026-09-22). 세 화면·라우트 모두 그대로 살아 있다.
      // 통합 화면이 기존 두 템플릿을 흡수하므로 그쪽을 먼저 두고, 원래 두 화면을 뒤에 붙인다.
      { key: "communication-templates", label: "Communication Templates", icon: <LayoutTemplate size={16} />, path: "/crm/communication-templates", status: "active" },
      { key: "email-templates", label: "Email", icon: <Mail size={16} />, path: "/crm/email-templates", status: "active" },
      { key: "slack-templates", label: "Slack", icon: <MessageSquare size={16} />, path: "/crm/slack-templates", status: "active" },
      // Lead Mail Rules 는 메뉴에서만 숨긴다(삭제 아님). 라우트·테이블·발송 규칙은 모두 그대로 돌아간다.
      // 되돌리려면 아래 한 줄의 주석을 풀면 된다.
      // { key: "lead-mail-rules", label: "Lead Mail Rules", icon: <Mail size={16} />, path: "/crm/lead-mail-rules", status: "active" },
    ],
  },
  {
    key: "test",
    label: "Test",
    icon: <FlaskConical size={18} />,
    // 테스트·실험 메뉴는 운영 메뉴와 섞지 않는다 — 슈퍼관리자만(docs/ux-laws/HICKS_LAW.md 규칙 6).
    //   메뉴 권한 관리(DB)에 등급이 따로 저장돼 있으면 그쪽이 이긴다(useMenuRoles: DB 우선).
    minRole: "super_admin",
    items: [
      { key: "email-send", label: "Email Send", icon: <Mail size={16} />, path: "/crm/automation/email-test", status: "active" },
      { key: "lead-api", label: "Lead API(VAULT)", icon: <FlaskConical size={16} />, path: "/crm/lead-test", status: "active" },
      { key: "proposal-form-sample", label: "제안서 양식 샘플", icon: <FileText size={16} />, path: "/crm/proposal-form-sample", status: "active" },
    ],
  },
  // {
  //   key: "automation",
  //   label: "Automation",
  //   items: [
  //     { key: "docusign", label: "DocuSign", icon: <PenLine size={16} />, path: "/crm/automation/docusign", status: "coming_soon", comingSoon: true },
  //     { key: "slack", label: "Slack", icon: <MessageSquare size={16} />, path: "/crm/automation/slack", status: "coming_soon", comingSoon: true },
  //     { key: "calendar", label: "Calendar", icon: <CalendarDays size={16} />, path: "/crm/automation/calendar", status: "coming_soon", comingSoon: true },
  //     { key: "drive", label: "Drive Storage", icon: <HardDrive size={16} />, path: "/crm/automation/drive", status: "coming_soon", comingSoon: true },
  //   ],
  // },
];

const STATUS_BADGE_MAP: Record<SideMenuStatus, FeatureStatus> = {
  active: "active",
  beta: "beta",
  development: "development",
  coming_soon: "coming_soon",
};

const CRM_SIDEBAR_LANGUAGE: Language = "en";

// 사이드바 섹션/그룹 펼침 상태 저장 키 — 접힌 key 집합을 JSON 배열로 보관.
const SIDEBAR_SECTIONS_KEY = "crm-sidebar-collapsed-sections";
const SIDEBAR_PINNED_KEY = "crm-sidebar-pinned";
const DEFAULT_PINNED = ["dashboard", "leads", "pipeline", "projects", "relationships"];
const MAX_PINNED = 7;
const SIDEBAR_GROUPS_KEY = "crm-sidebar-collapsed-groups";

// 저장된 접힘 key 집합 읽기 — 저장값 없으면 null(호출부가 기본값 결정).
function readStoredKeySet(storageKey: string): Set<string> | null {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved === null) return null;
    const parsed = JSON.parse(saved);
    if (Array.isArray(parsed)) return new Set(parsed.map(String));
  } catch { /* storage 차단/파싱 실패 — 기본값 사용 */ }
  return null;
}

function writeStoredKeySet(storageKey: string, value: Set<string>): void {
  try { localStorage.setItem(storageKey, JSON.stringify(Array.from(value))); } catch { /* 무시 */ }
}

// 1-b: 메뉴 노출은 DB 매핑(menu_role_map) 우선, 없으면 코드 상수(minRole) 폴백 — useMenuRoles().visible

export default function CRMLayout() {
  // 마스터 데이터(Application·Opportunity 상태 — 설정 > 관리자 > 마스터 데이터)를 받으면 아래 화면을 다시 그린다
  useMasters();
  const { language, t } = useLanguage();
  const [helpOpen, setHelpOpen] = useState(false);
  const { visible, overrides, getEffectiveMinRole } = useMenuRoles();
  const { role } = useAuth();
  // 문서 사용여부 — 메뉴의 사용/미사용과 **별개** 다.
  const { isEnabled: documentEnabled } = useDocumentCatalog();
  const routeLocation = useLocation();
  // 메뉴 관리(/settings/menus)에서 바꾼 이름·링크·소속·순서를 코드 카탈로그에 입힌 결과.
  // 오버라이드가 없으면 MENU_SECTIONS 그대로다 — 구조가 DB 때문에 사라지는 일은 없다.
  const sections = useMemo(
    () => applyMenuOverrides(MENU_SECTIONS, overrides, "crm", <Dot size={16} />, (name) => renderMenuIcon(name, 16)),
    [overrides]
  );
  // IT-CONSULTING 으로 옮겨 간 CRM 메뉴 — 사이드바엔 없지만 화면(경로)은 여기서 열린다.
  //   제목·권한 재검사가 "모르는 화면"으로 빠지지 않게 따로 들고 있는다.
  const movedAway = useMemo(() => itemsMovedAwayFrom("crm", overrides, resolveMenuLabel), [overrides]);
  // 상단 컨텍스트 바 좌측 = 라우트 기반 페이지명(사이드바 메뉴 라벨 정의 재사용). 매칭 실패 시 빈 값.
  // 페이지 헤더 제목(DESIGN-AI §19) = 메뉴 관리의 "페이지 제목" || 메뉴 이름 — PageMetaProvider 로 내려준다.
  // ★ sections 를 의존성에 넣는다 — 빠져 있어서 오버라이드가 늦게 와도 바의 이름이 안 바뀌었다.
  const pageMeta = useMemo<PageMeta | null>(() => {
    const path = routeLocation.pathname;
    const titleOf = (x: unknown) => (x as { pageTitle?: string }).pageTitle;
    const cand: Array<{ p: string; label: string; pageTitle?: string; section: string; sectionTo?: string; icon?: React.ReactNode; area?: string }> = [];
    for (const s of sections) {
      // 상단 바 경로는 사이드바와 같은 글자로 — 사이드바가 영어 고정이라 같은 언어를 쓴다.
      const section = getCrmSectionLabel(CRM_SIDEBAR_LANGUAGE, s.key, s.label);
      // 경로의 섹션 칸을 누르면 그 섹션의 첫 메뉴로 간다.
      const sectionTo = s.items?.[0]?.path ?? s.extraItems?.[0]?.path;
      // 제목 기본값 = 사이드바에 보이는 메뉴 이름(사이드바는 영어 고정) — 제목과 LNB 이름이 같아야 한다.
      for (const it of [...(s.items ?? []), ...(s.extraItems ?? [])]) cand.push({ p: it.path, label: getCrmMenuLabel(CRM_SIDEBAR_LANGUAGE, it.key, it.label), pageTitle: titleOf(it), section, sectionTo, icon: it.icon });
      if (s.docEntries) {
        for (const entry of s.docEntries) {
          if ("type" in entry && entry.type === "group") {
            for (const child of entry.children) cand.push({ p: docEntryPath(child), label: getCrmDocumentLabel(language, child.key, child.label), pageTitle: titleOf(child), section, sectionTo });
          } else if (!("type" in entry)) {
            cand.push({ p: docEntryPath(entry), label: getCrmDocumentLabel(language, entry.key, entry.label), pageTitle: titleOf(entry), section, sectionTo });
          }
        }
      }
    }
    for (const m of movedAway) {
      cand.push({ p: m.path, label: m.label, pageTitle: m.pageTitle, section: sectionLabelOf(m.sectionKey, overrides, resolveMenuLabel), icon: renderMenuIcon(m.icon, 16), area: "IT-CONSULTING" });
    }
    const match = cand.filter((c) => c.p && (path === c.p || path.startsWith(c.p + "/"))).sort((a, b) => b.p.length - a.p.length)[0];
    if (!match) return null;
    // 경로 칸은 눌러서 이동한다 — 영역 → 영역 첫 화면, 섹션 → 섹션 첫 메뉴.
    //   상세 화면(메뉴 경로 아래, 예: /crm/opportunities/:id)이면 목록 메뉴 칸을 하나 더 붙여 바로 돌아가게 한다.
    //   (페이지 이름은 여전히 넣지 않는다 — 상세의 목록 메뉴는 상위 단계다.)
    const crumbs: Crumb[] = [
      match.area ? { label: match.area } : { label: "CRM", to: "/crm/dashboard" },
      { label: match.section, to: match.area ? undefined : match.sectionTo },
    ];
    if (path !== match.p) crumbs.push({ label: match.label, to: match.p });
    return { menuLabel: match.label, title: pageTitleOf(match.label, match.pageTitle), crumbs, icon: match.icon };
  }, [routeLocation.pathname, language, sections, movedAway, overrides]);
  const pageLabel = pageMeta?.menuLabel ?? "";
  // 경로별 접근 가드 — 현재 경로에 해당하는 메뉴 항목의 권한을 재검사(상속 적용).
  // 메뉴엔 안 보여도 URL 직접 접근하는 상위 권한 페이지를 차단한다. 매핑 없는 경로는 CRM 진입 권한으로 허용.
  // 권한 요청 버튼이 어떤 메뉴에 대한 요청인지 알아야 해서 boolean 대신 차단된 메뉴 키를 돌려준다.
  const deniedKey = useMemo(() => {
    let bestKey: string | null = null;
    let bestFallback: Role | undefined;
    let bestLen = -1;
    for (const section of sections) {
      const items = [...(section.items ?? []), ...(section.extraItems ?? [])];
      for (const it of items) {
        if (routeLocation.pathname.startsWith(it.path) && it.path.length > bestLen) {
          bestKey = itemMenuKeyOf(section, it);
          bestFallback = it.minRole;
          bestLen = it.path.length;
        }
      }
    }
    // IT-CONSULTING 으로 옮겨 간 메뉴의 화면도 같은 키로 재검사한다 — 빠지면 URL 로 그냥 열린다.
    for (const m of movedAway) {
      if (routeLocation.pathname.startsWith(m.path) && m.path.length > bestLen) {
        bestKey = m.menuKey;
        bestFallback = m.minRole as Role | undefined;
        bestLen = m.path.length;
      }
    }
    // ★ 문서는 **메뉴와 다른 축**으로 판정한다.
    //
    //   예전엔 문서 경로를 메뉴 키로 바꿔 visible() 에 그대로 넘겼다. visible() 은 등급뿐 아니라
    //   메뉴의 사용/미사용(menu_role_map.enabled, 조상 상속 포함)까지 보기 때문에,
    //   메뉴 관리에서 "Dematic MOU" 메뉴를 끄자 /crm/documents/mou-dematic 이 통째로
    //   "접근 권한이 없습니다" 가 됐다(사용자 지적).
    //   문서는 이제 메뉴별로 흩어진 페이지가 아니라 한 화면에 모인 카탈로그다 —
    //   사이드바에 안 띄우는 것과 문서를 못 쓰게 막는 것은 다른 결정이어야 한다.
    //
    //   그래서 문서 경로는:
    //     · 등급   → menu_role_map 의 같은 키 (운영 중인 설정을 그대로 쓴다)
    //     · 사용여부 → crm_document_catalog.enabled (메뉴와 무관)
    //   두 가지만 본다. 메뉴의 enabled·상속은 보지 않는다.
    const docKey = documentKeyForPath(routeLocation.pathname);
    if (docKey) {
      const documentKey = documentMenuRoleKey(docKey);
      if (!documentEnabled(docKey)) return documentKey;
      const required = getEffectiveMinRole(documentKey, "viewer");
      const allowed = role === "super_admin" || ROLE_RANK[role ?? "viewer"] >= ROLE_RANK[required];
      return allowed ? null : documentKey;
    }
    return bestKey && !visible(bestKey, bestFallback) ? bestKey : null;
  }, [routeLocation.pathname, visible, getEffectiveMinRole, documentEnabled, role, sections, movedAway]);
  const accessDenied = deniedKey !== null;
  const isEmbedded = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embedded") === "1";
  const isMobile = useIsMobile();
  // 모바일 웹앱(PWA) — 767px 이하는 사이드바·상단 바 대신 모바일 셸(src/mobile)로 감싼다.
  //   본문은 PC 와 같은 실제 화면(<Outlet/>). 768px(태블릿)부터는 아래 기존 분기 그대로다.
  const isPhone = useIsMobile(767);
  // ⌘K 빠른 추가(데스크톱) — state.quickCreate 로 도착하면 본문의 주요 버튼([data-primary-action])을 한 번 누른다.
  //   폰은 모바일 셸(MobileFab)이 같은 일을 한다. 화면이 스스로 처리하고 표시를 지웠으면(기회·일정) 누르지 않는다.
  const quickNavigate = useNavigate();
  const quickCreate = !isPhone && !!(routeLocation.state as { quickCreate?: boolean } | null)?.quickCreate;
  useEffect(() => {
    if (!quickCreate) return undefined;
    const path = routeLocation.pathname + routeLocation.search;
    const start = Date.now();
    const poll = window.setInterval(() => {
      const live = (window.history.state as { usr?: { quickCreate?: boolean } } | null)?.usr?.quickCreate;
      if (!live) { window.clearInterval(poll); return; }
      const el = document.querySelector<HTMLElement>("main [data-primary-action]:not([disabled])");
      if (el || Date.now() - start >= 4000) {
        window.clearInterval(poll);
        el?.click();
        if (window.location.pathname + window.location.search === path) quickNavigate(path, { replace: true, state: null });
      }
    }, 150);
    return () => window.clearInterval(poll);
  }, [quickCreate, routeLocation.pathname, routeLocation.search, quickNavigate]);
  // 접힘 기본값: 모바일은 항상 접힘(본문 공간 확보), 데스크톱은 수동 토글 저장값 우선,
  // 없으면 1280px 미만 접힘 (Phase 3.5 확정)
  const [collapsed, setCollapsed] = useState(() => {
    if (isMobileViewport()) return true;
    try {
      const saved = localStorage.getItem("crm-sidebar-collapsed");
      if (saved !== null) return saved === "1";
    } catch { /* storage 차단 환경 — 뷰포트 기본값 사용 */ }
    return typeof window !== "undefined" && window.innerWidth < 1280;
  });
  function toggleCollapsed() {
    setCollapsed((value) => {
      const next = !value;
      // 모바일의 임시 펼침/접힘은 저장하지 않는다 — 데스크톱 선호값 보존
      if (!isMobileViewport()) {
        try { localStorage.setItem("crm-sidebar-collapsed", next ? "1" : "0"); } catch { /* 무시 */ }
      }
      return next;
    });
  }
  // 1depth 섹션은 최초 진입 시 전부 접힘(기본값). 이후 사용자가 펼친/접은 상태는
  // localStorage에 저장돼 새로고침·페이지 이동 후에도 유지된다(수동 접기 전까지 펼침 유지).
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    // 최초 1회만 쓰는 초기값이라 코드 카탈로그 기준으로 둔다. 직접 추가한 섹션은 펼친 상태로 시작한다.
    () => readStoredKeySet(SIDEBAR_SECTIONS_KEY) ?? new Set(MENU_SECTIONS.map((s) => s.key))
  );
  // 문서 자동화 하위 그룹(견적서/계약서/MOU)도 동일하게 펼침 상태 유지. 기본값은 펼침(빈 set).
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => readStoredKeySet(SIDEBAR_GROUPS_KEY) ?? new Set()
  );
  const [comingSoon, setComingSoon] = useState<{ open: boolean; featureName: string } | null>(null);
  const [navQuery, setNavQuery] = useState("");
  // 검색 단축키(Cmd/Ctrl+K) — 어디서나 명령 팔레트(메뉴 + 리드·기회·회사 + 빠른 동작)를 연다. 다시 누르면 닫힌다.
  //   사이드바 메뉴 검색 칸은 그대로 둔다(칸에 직접 쳐서 메뉴만 거르는 기존 동작).
  //   입력 중인 다른 칸의 타이핑은 막지 않는다 — Cmd/Ctrl+K 조합만 가로챈다.
  const navSearchRef = useRef<HTMLInputElement>(null);
  const focusSearchPending = useRef(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    if (isEmbedded) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isMenuSearchShortcut(e)) return;
      e.preventDefault();
      setPaletteOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEmbedded]);
  useEffect(() => {
    if (!collapsed && focusSearchPending.current) {
      focusSearchPending.current = false;
      navSearchRef.current?.focus();
    }
  }, [collapsed]);

  // 펼침/접힘 상태 변경 시 저장(수동 토글 이후엔 유지) — 다음 방문/이동에서 복원.
  useEffect(() => { writeStoredKeySet(SIDEBAR_SECTIONS_KEY, collapsedSections); }, [collapsedSections]);
  useEffect(() => { writeStoredKeySet(SIDEBAR_GROUPS_KEY, collapsedGroups); }, [collapsedGroups]);

  function toggleSection(key: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleGroup(key: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleItemClick(item: SideMenuItem, event: React.MouseEvent) {
    // 리뷰 #56: 라우트 없는 '준비 중'(comingSoon/development) 항목은 무안내 리다이렉트 대신 안내 모달.
    if (item.comingSoon || item.status === "development" || item.status === "coming_soon") {
      event.preventDefault();
      setComingSoon({ open: true, featureName: item.label });
      return;
    }
    // 모바일: 메뉴 선택 후 오버레이 사이드바를 닫아 본문을 바로 보여준다
    if (isMobile) setCollapsed(true);
  }

  // 접힘(아이콘 전용) 상태에서 섹션 아이콘 클릭 → 사이드바를 펼치고 해당 섹션을 연다.
  function expandAndOpenSection(sectionKey: string) {
    setCollapsed(false);
    if (!isMobileViewport()) {
      try { localStorage.setItem("crm-sidebar-collapsed", "0"); } catch { /* 무시 */ }
    }
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    });
  }

  // 현재 경로가 이 섹션에 속하면 섹션 아이콘을 active 로 표시.
  function isSectionActive(section: SideMenuSection): boolean {
    const items = [...(section.items ?? []), ...(section.extraItems ?? [])];
    if (items.some((it) => routeLocation.pathname.startsWith(it.path))) return true;
    if (section.key === "document-automation" && routeLocation.pathname.startsWith("/crm/documents")) return true;
    if (section.docEntries && routeLocation.pathname.startsWith("/crm/document-automation/")) return true;
    return false;
  }

  // 지금 화면이 속한 섹션은 펼친다 — 처음 들어오면 섹션이 모두 접혀 있어 어디를 열지 추측해야 했다.
  //   (docs/ux-laws/HICKS_LAW.md 규칙 6 · JAKOBS_LAW.md 규칙 4) 사용자가 접은 다른 섹션은 그대로 둔다.
  useEffect(() => {
    const active = sections.find((sec) => isSectionActive(sec));
    if (!active) return;
    setCollapsedSections((prev) => {
      if (!prev.has(active.key)) return prev;
      const next = new Set(prev);
      next.delete(active.key);
      return next;
    });
  }, [routeLocation.pathname, sections]);

  // 자주 쓰는 메뉴 — 매일 쓰는 것은 섹션을 열지 않고 바로(힉스 · 피츠). 보는 사람 브라우저에만 남긴다.
  const [pinnedKeys, setPinnedKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_PINNED_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) return arr.filter((k) => typeof k === "string");
    } catch { /* storage 차단 — 기본값 */ }
    return DEFAULT_PINNED;
  });
  const [pinEditing, setPinEditing] = useState(false);
  const togglePin = (key: string) => setPinnedKeys((prev) => {
    const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key].slice(0, MAX_PINNED);
    try { localStorage.setItem(SIDEBAR_PINNED_KEY, JSON.stringify(next)); } catch { /* 무시 */ }
    return next;
  });
  const pinnableItems = useMemo(() => sections
    .filter((section) => (section.key !== "settings" || role === "super_admin") && visible(sectionMenuKeyOf(section), section.minRole))
    .flatMap((section) => [...(section.items ?? []), ...(section.extraItems ?? [])]
      .filter((item) => visible(itemMenuKeyOf(section, item), item.minRole) && !item.comingSoon)
      .map((item) => ({ ...item, label: getCrmMenuLabel(CRM_SIDEBAR_LANGUAGE, item.key, item.label) }))),
  [sections, role, visible]);
  // 메뉴 경로 전부 — 더 긴 형제 메뉴(/crm/partners/analysis)가 맞으면 짧은 쪽(/crm/partners)은 강조하지 않는다
  const menuPaths = useMemo(() => pinnableItems.map((item) => item.path), [pinnableItems]);
  // 모바일 더보기 = 사이드바와 같은 전체 메뉴(섹션별, 같은 권한 검사). 문서 묶음은 펼쳐서 넣는다.
  const mobileMenuGroups = useMemo(() => sections
    .filter((section) => (section.key !== "settings" || role === "super_admin") && visible(sectionMenuKeyOf(section), section.minRole))
    .map((section) => {
      const items: { key: string; label: string; path: string }[] = [];
      const pushItem = (item: SideMenuItem) => {
        if (visible(itemMenuKeyOf(section, item), item.minRole) && !item.comingSoon) items.push({ key: item.key, label: getCrmMenuLabel(CRM_SIDEBAR_LANGUAGE, item.key, item.label), path: item.path });
      };
      if (section.docEntries) {
        for (const entry of section.docEntries) {
          if ("type" in entry && entry.type === "group") {
            for (const child of entry.children) {
              if (visible(`crm:${section.key}:${child.key}`)) items.push({ key: `document-${child.key}`, label: getCrmDocumentLabel(CRM_SIDEBAR_LANGUAGE, child.key, child.label), path: docEntryPath(child) });
            }
          } else if (visible(`crm:${section.key}:${entry.key}`)) {
            items.push({ key: `document-${entry.key}`, label: getCrmDocumentLabel(CRM_SIDEBAR_LANGUAGE, entry.key, entry.label), path: docEntryPath(entry as DocumentMenuItem) });
          }
        }
      } else {
        section.items?.forEach(pushItem);
      }
      section.extraItems?.forEach(pushItem);
      return { key: section.key, label: getCrmSectionLabel(CRM_SIDEBAR_LANGUAGE, section.key, section.label), items };
    })
    .filter((g) => g.items.length > 0),
  [sections, role, visible]);
  const pinnedItems = pinnedKeys.map((k) => pinnableItems.find((it) => it.key === k)).filter(Boolean) as SideMenuItem[];

  // Vault UI 활성 막대 — 활성 메뉴(NavLink 의 .active) 세로 중앙으로 moveIndicator 가 옮긴다.
  //   매 렌더 확인하되 목표 위치가 같으면 건드리지 않는다(섹션 접기·검색·핀 편집으로 위치가 바뀌어도 따라감).
  //   접힘(아이콘만)·활성 메뉴 없음이면 숨기고, 다시 보일 때는 늘어남 없이 제자리에서 시작한다.
  const navRef = useRef<HTMLElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const nav = navRef.current;
    const ind = indicatorRef.current;
    if (!nav || !ind) return;
    const active = Array.from(nav.querySelectorAll<HTMLElement>("a.active")).find((a) => a.offsetParent === nav) ?? null;
    if (!active) {
      ind.style.opacity = "0";
      delete ind.dataset.y;
      return;
    }
    const y = active.offsetTop + (active.offsetHeight - ind.offsetHeight) / 2;
    if (ind.dataset.y === String(y) && ind.style.opacity === "1") return;
    ind.style.opacity = "1";
    moveIndicator(ind, active);
  });

  if (isPhone && !isEmbedded) {
    return (
      <MobileApp
        page={<PageMetaProvider value={pageMeta}><RouteSuspense><Outlet /></RouteSuspense></PageMetaProvider>}
        pageMeta={pageMeta}
        accessDenied={accessDenied}
        deniedNotice={<AccessDeniedNotice menuKey={deniedKey} menuLabel={pageLabel} />}
        groups={mobileMenuGroups}
      />
    );
  }

  if (isEmbedded) {
    return (
      <main data-crm-lang={language} style={{ minHeight: "100vh", background: "var(--crm-bg)", overflow: "auto" }}>
        {/* 런타임 번역기는 App.PageLayoutWithCrmTranslation(외부 단일 래퍼)가 전담 — 중복 래핑 제거(perf) */}
        <RouteSuspense><Outlet /></RouteSuspense>
      </main>
    );
  }

  // iconOnly = 아이콘만 보이는 상태 = 접힘. 접힘/펼침은 오직 토글 버튼으로만 전환한다
  // (마우스 호버로는 펼치지 않음 — 사용자 요청). 버튼을 누르기 전까지 현 상태 유지.
  const iconOnly = collapsed;

  return (
    <div data-crm-lang={language} className="app-layout-shell" style={{ ...layoutStyle, position: "relative" }}>
      {/* 런타임 번역기는 App.PageLayoutWithCrmTranslation(외부 단일 래퍼)가 전담 — 중복 래핑 제거(perf) */}
      {/* 접힘 모드: aside 가 absolute 오버레이가 되므로 52px 슬롯을 고정해 리플로우 방지.
          모바일은 펼침 상태도 오버레이(본문을 밀지 않음)라 슬롯을 항상 유지한다. */}
      {isMobile ? <div style={{ width: 56, minWidth: 56, flexShrink: 0 }} aria-hidden /> : null}
      {/* 모바일 펼침: 본문 위 백드롭 — 탭하면 닫힘 */}
      {isMobile && !collapsed ? (
        <div
          onClick={() => setCollapsed(true)}
          aria-hidden
          style={{ position: "absolute", inset: 0, zIndex: "var(--z-sticky)" as unknown as number, background: "rgba(15,23,42,0.35)" }}
        />
      ) : null}
      {<aside
        data-crm-i18n-skip="true"
        style={{
          ...sidebarStyle,
          width: iconOnly ? 56 : 240,
          minWidth: iconOnly ? 56 : 240,
          ...(isMobile
            ? {
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: "var(--z-sticky)" as unknown as number,
                boxShadow: !collapsed && isMobile ? "12px 0 32px rgba(15,23,42,0.18)" : "none",
              }
            : null),
        }}
      >
        <div style={{ ...sidebarHeaderStyle, flexDirection: "column", alignItems: "stretch", gap: 8, justifyContent: iconOnly ? "center" : "flex-start", padding: iconOnly ? "14px 0" : "12px 12px 10px" }}>
          {!iconOnly ? (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <ProductSwitcher current="crm" />
                <button type="button" onClick={toggleCollapsed} title={t({ ko: "메뉴 접기", en: "Collapse menu" })} style={collapseButtonStyle}>
                  <PanelLeftClose size={16} />
                </button>
              </div>
              <MenuSearchInput
                inputRef={navSearchRef}
                value={navQuery}
                onChange={setNavQuery}
                placeholder={t("메뉴 검색", "Search menu")}
              />
            </>
          ) : (
            <button
              type="button"
              onClick={toggleCollapsed}
              title={t({ ko: "메뉴 펼치기", en: "Expand menu" })}
              style={collapseButtonStyle}
            >
              <PanelLeftOpen size={16} />
            </button>
          )}
        </div>

        <MenuPathsCtx.Provider value={menuPaths}>
        <nav ref={navRef} style={navStyle}>
          {/* 활성 막대 — 폭 3 · 높이 20 · primary · 오른쪽만 둥글게. 접힘(아이콘만)에서는 그리지 않는다. */}
          {!iconOnly ? <span ref={indicatorRef} aria-hidden style={navIndicatorStyle} /> : null}
          {!iconOnly && navQuery.trim() ? (() => {
            const qq = navQuery.trim().toLowerCase();
            const flat = sections
              .filter((section) => (section.key !== "settings" || role === "super_admin") && visible(sectionMenuKeyOf(section), section.minRole))
              .flatMap((section) => [...(section.items ?? []), ...(section.extraItems ?? [])]
                .filter((item) => visible(itemMenuKeyOf(section, item), item.minRole))
                .map((item) => ({
                  ...item,
                  label: getCrmMenuLabel(CRM_SIDEBAR_LANGUAGE, item.key, item.label),
                  _q: `${getCrmMenuLabel("ko" as Language, item.key, item.label)} ${getCrmMenuLabel("en" as Language, item.key, item.label)} ${item.key}`.toLowerCase(),
                })));
            const matched = flat.filter((item) => item._q.includes(qq));
            return matched.length
              ? matched.map((item) => <SideMenuItemButton key={item.key} item={item} collapsed={false} onClick={handleItemClick} />)
              : <div style={{ padding: "12px 16px", fontSize: 12, color: "var(--crm-text-muted)" }}>검색 결과 없음</div>;
          })() : (<>
          {!iconOnly && (pinnedItems.length > 0 || pinEditing) ? (
            <div style={{ marginBottom: 6, paddingBottom: 6, borderBottom: "1px solid var(--hairline)" }}>
              <div style={{ ...sectionButtonStyle, cursor: "default" }}>
                <span style={sectionLabelStyle}>{t({ ko: "자주 쓰는 메뉴", en: "Pinned" })}</span>
                <button type="button" onClick={() => setPinEditing((v) => !v)} aria-pressed={pinEditing}
                  style={{ height: 24, padding: "0 6px", border: 0, borderRadius: 6, background: "transparent", color: pinEditing ? "var(--crm-blue)" : "var(--crm-text-muted)", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {pinEditing ? t({ ko: "완료", en: "Done" }) : t({ ko: "편집", en: "Edit" })}
                </button>
              </div>
              {pinEditing ? (
                <div style={{ display: "grid", gap: 0, padding: "0 8px", maxHeight: 260, overflowY: "auto" }}>
                  {pinnableItems.map((item) => {
                    const on = pinnedKeys.includes(item.key);
                    const full = !on && pinnedKeys.length >= MAX_PINNED;
                    return (
                      <label key={item.key} style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 32, padding: "0 6px", borderRadius: 6, fontSize: 13, color: full ? "var(--crm-text-muted)" : "var(--crm-text-primary)", cursor: full ? "not-allowed" : "pointer" }}>
                        <input type="checkbox" checked={on} disabled={full} onChange={() => togglePin(item.key)} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
                      </label>
                    );
                  })}
                  <div style={{ padding: "4px 6px", fontSize: 12, color: "var(--crm-text-muted)" }}>{t({ ko: `최대 ${MAX_PINNED}개 · 이 브라우저에만 저장`, en: `Up to ${MAX_PINNED} · saved in this browser` })}</div>
                </div>
              ) : pinnedItems.map((item) => (
                <SideMenuItemButton key={`pin-${item.key}`} item={item} collapsed={false} onClick={handleItemClick} />
              ))}
            </div>
          ) : null}
          {sections.filter((section) => (section.key !== "settings" || role === "super_admin") && visible(sectionMenuKeyOf(section), section.minRole)).map((section) => {
            const isSectionCollapsed = collapsedSections.has(section.key);
            // 접힘 상태: 섹션(1depth) 대표 아이콘 1개만 — 클릭 시 펼치며 해당 섹션 오픈.
            if (iconOnly) {
              return (
                <SideSectionIconButton
                  key={section.key}
                  icon={section.icon}
                  label={getCrmSectionLabel(CRM_SIDEBAR_LANGUAGE, section.key, section.label)}
                  active={isSectionActive(section)}
                  onClick={() => expandAndOpenSection(section.key)}
                />
              );
            }
            return (
              <div key={section.key} style={{ marginBottom: 2 }}>
                <button type="button" onClick={() => toggleSection(section.key)} style={sectionButtonStyle}>
                  <span style={sectionLabelStyle}>{getCrmSectionLabel(CRM_SIDEBAR_LANGUAGE, section.key, section.label)}</span>
                  <ChevronDown size={12} style={{ transform: isSectionCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
                </button>

                {!isSectionCollapsed && (
                  <>
                    {section.docEntries
                      ? <DocEntriesRenderer
                          entries={section.docEntries}
                          sectionKey={section.key}
                          isVisible={(childKey) => visible(`crm:${section.key}:${childKey}`)}
                          collapsed={iconOnly}
                          collapsedGroups={collapsedGroups}
                          onToggleGroup={toggleGroup}
                          onItemClick={handleItemClick}
                          language={CRM_SIDEBAR_LANGUAGE}
                        />
                      : section.items?.filter((item) => visible(itemMenuKeyOf(section, item), item.minRole)).map((item) => (
                          <SideMenuItemButton
                            key={item.key}
                            item={{ ...item, label: getCrmMenuLabel(CRM_SIDEBAR_LANGUAGE, item.key, item.label) }}
                            collapsed={iconOnly}
                            onClick={handleItemClick}
                          />
                        ))
                    }
                    {/* Extra items rendered after docEntries (e.g. 고객 자료 분석) */}
                    {section.extraItems?.filter((item) => visible(itemMenuKeyOf(section, item), item.minRole)).map((item) => (
                      <SideMenuItemButton
                        key={item.key}
                        item={{ ...item, label: getCrmMenuLabel(CRM_SIDEBAR_LANGUAGE, item.key, item.label) }}
                        collapsed={iconOnly}
                        onClick={handleItemClick}
                      />
                    ))}
                  </>
                )}
              </div>
            );
          })}
          </>)}
        </nav>
        </MenuPathsCtx.Provider>
        {/* 접힌 메뉴(56px)에서도 계정은 남긴다 — 아바타만, 같은 메뉴가 열린다. */}
        <AccountMenu variant={iconOnly ? "compact" : "sidebar"} />
      </aside>}

      <main style={mainStyle}>
        {/* 얇은 상단 컨텍스트 바(공용 AppContextBar) — 좌: 페이지명 / 우: 피드백·도움말·알림·한영 */}
        {/* DESIGN-AI §1 — 상단 바는 경로만. 페이지 이름은 페이지 제목(PageHeader)에 한 번만 나온다. */}
        <AppContextBar crumbs={pageMeta?.crumbs ?? ["CRM"]}>
          <button type="button" onClick={() => setHelpOpen(true)} title={t({ ko: "도움말", en: "Help" })} className={contextActionBtnClass}>
            <HelpCircle size={16} />
            {/* 폰에서는 글자를 접고 아이콘만 — 상단 바 폭 확보(mobile.css) */}
            <span className="app-context-bar__actionLabel">{t({ ko: "도움말", en: "Help" })}</span>
          </button>
        </AppContextBar>
        {accessDenied ? (
          <AccessDeniedNotice menuKey={deniedKey} menuLabel={pageLabel} />
        ) : (
          <PageMetaProvider value={pageMeta}><RouteSuspense><Outlet /></RouteSuspense></PageMetaProvider>
        )}
      </main>

      {comingSoon?.open ? (
        <ComingSoonModal
          open={comingSoon.open}
          featureName={comingSoon.featureName}
          featureStatus="coming_soon"
          onClose={() => setComingSoon(null)}
        />
      ) : null}
      <HelpAssistantDrawer open={helpOpen} onOpenChange={setHelpOpen} />
      {paletteOpen ? <CommandPalette menuItems={pinnableItems} onClose={() => setPaletteOpen(false)} /> : null}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * ⌘K 명령 팔레트 — 메뉴 · 리드 · 기회 · 회사 · 동작을 한 칸에서 찾는다.
 *   레코드는 처음 열 때 받아 둔다(2분 지나면 다시). 받는 중·실패해도 메뉴·동작은 바로 쓸 수 있다.
 *   묶음마다 최대 5개 · ↑↓ 이동 · Enter 실행 · Esc 닫기. 열릴 때 150ms 페이드 + 0.96→1(동작 줄이기면 바로).
 *   회사는 /api/sales/companies(정본) 만 쓰고, 비활성 회사는 뺀다. 링크는 /crm/relationships?q=.
 * ────────────────────────────────────────────────────────────────────────── */
type PaletteGroup = "menu" | "lead" | "opp" | "company" | "action";
type PaletteItem = { id: string; group: PaletteGroup; label: string; sub?: string; run: () => void };
type PaletteRecords = {
  leads: { id: string; company: string; leadName: string }[];
  opps: { id: string; name: string; company: string }[];
  companies: { key: string; name: string }[];
};
const PALETTE_LIMIT = 5;
let paletteCache: { at: number; data: PaletteRecords } | null = null;

function CommandPalette({ menuItems, onClose }: { menuItems: Array<{ key: string; label: string; path: string }>; onClose: () => void }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [records, setRecords] = useState<PaletteRecords | null>(paletteCache?.data ?? null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 여는 순간 — 포커스·모션. 닫으면 열기 전 포커스로 돌려 준다.
  useLayoutEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    backdropRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: motionT(DUR.fast), easing: EASE.out });
    panelRef.current?.animate([{ opacity: 0, transform: "scale(0.96)" }, { opacity: 1, transform: "none" }], { duration: motionT(DUR.fast), easing: EASE.out });
    return () => { prevFocus?.focus?.(); };
  }, []);

  // 레코드 — 처음 열 때(또는 2분 지난 캐시) 한 번 받는다. 셋 중 실패한 것만 비운다.
  useEffect(() => {
    if (paletteCache && Date.now() - paletteCache.at < 120000) return undefined;
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      fetchLeadInboxLeads(),
      fetch("/api/sales/projects").then((r) => (r.ok ? r.json() : { projects: [] })),
      fetchCompanies(),
    ]).then(([leadsR, oppsR, compR]) => {
      const data: PaletteRecords = {
        leads: leadsR.status === "fulfilled"
          ? leadsR.value.map((l) => ({ id: String(l.id), company: String(l.company || ""), leadName: String(l.leadName || "") }))
          : [],
        opps: oppsR.status === "fulfilled"
          ? ((oppsR.value?.projects || []) as Array<{ id: string; project_name?: string; account_name?: string | null; status?: string }>)
            .filter((p) => p.status !== "cancelled")
            .map((p) => ({ id: p.id, name: p.project_name || "", company: p.account_name || "" }))
          : [],
        companies: compR.status === "fulfilled"
          ? compR.value.companies.filter((c) => c.is_active !== false && c.name).map((c) => ({ key: c.key || c.id, name: String(c.name) }))
          : [],
      };
      paletteCache = { at: Date.now(), data };
      if (alive) { setRecords(data); setLoading(false); }
    });
    return () => { alive = false; };
  }, []);

  const go = (path: string, state?: unknown) => { onClose(); navigate(path, state ? { state } : undefined); };

  const groups = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const has = (...parts: Array<string | null | undefined>) => !qq || parts.some((p) => (p || "").toLowerCase().includes(qq));
    const menuQ = (it: { key: string; label: string }) =>
      `${getCrmMenuLabel("ko" as Language, it.key, it.label)} ${getCrmMenuLabel("en" as Language, it.key, it.label)} ${it.key}`;
    const actions: PaletteItem[] = [
      { id: "a:lead", group: "action", label: t("리드 추가", "Add lead"), run: () => go("/crm/leads", { quickCreate: true }) },
      { id: "a:opp", group: "action", label: t("기회 추가", "Add opportunity"), run: () => go("/crm/opportunities", { quickCreate: true }) },
      { id: "a:event", group: "action", label: t("일정 추가", "Add event"), run: () => go("/crm/calendar", { quickCreate: true }) },
      { id: "a:company", group: "action", label: t("회사 추가", "Add company"), run: () => go("/crm/relationships", { quickCreate: true }) },
    ];
    const out: Array<{ group: PaletteGroup; title: string; items: PaletteItem[] }> = [];
    // 빈 칸이면 동작만 먼저 보여 준다(레코드 전체를 늘어놓지 않는다).
    if (qq) {
      out.push({ group: "menu", title: t("메뉴", "Menus"), items: menuItems.filter((it) => menuQ(it).toLowerCase().includes(qq)).slice(0, PALETTE_LIMIT)
        .map((it) => ({ id: `m:${it.key}`, group: "menu" as const, label: it.label, sub: it.path, run: () => go(it.path) })) });
      if (records) {
        out.push({ group: "lead", title: t("리드", "Leads"), items: records.leads.filter((l) => has(l.company, l.leadName)).slice(0, PALETTE_LIMIT)
          .map((l) => ({ id: `l:${l.id}`, group: "lead" as const, label: l.company || l.leadName, sub: l.company && l.leadName ? l.leadName : undefined, run: () => go("/crm/leads", { leadId: l.id }) })) });
        out.push({ group: "opp", title: t("기회", "Opportunities"), items: records.opps.filter((o) => has(o.name, o.company)).slice(0, PALETTE_LIMIT)
          .map((o) => ({ id: `o:${o.id}`, group: "opp" as const, label: o.name || o.company, sub: o.company && o.company !== o.name ? o.company : undefined, run: () => go(`/crm/opportunities/${o.id}`) })) });
        out.push({ group: "company", title: t("회사", "Companies"), items: records.companies.filter((c) => has(c.name)).slice(0, PALETTE_LIMIT)
          .map((c) => ({ id: `c:${c.key}`, group: "company" as const, label: c.name, run: () => go(`/crm/relationships?q=${encodeURIComponent(c.name)}`) })) });
      }
    }
    out.push({ group: "action", title: t("동작", "Actions"), items: actions.filter((a) => has(a.label)).slice(0, PALETTE_LIMIT) });
    return out.filter((g) => g.items.length > 0);
  }, [q, records, menuItems, t]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-palette-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (flat.length) setActive((i) => (i + 1) % flat.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (flat.length) setActive((i) => (i - 1 + flat.length) % flat.length); }
    else if (e.key === "Enter") { e.preventDefault(); flat[active]?.run(); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
  };

  let idx = -1;
  return (
    <div ref={backdropRef} className="ds-scope fixed inset-0 flex items-start justify-center bg-[rgba(15,23,42,0.32)] px-4 pt-[12vh]"
      style={{ zIndex: "var(--z-modal, 1000)" as unknown as number }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={t("검색", "Search")}
        className="flex max-h-[min(560px,76vh)] w-full max-w-[600px] flex-col overflow-hidden rounded-card border border-gray-200 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.24)]">
        <div className="flex items-center gap-2 border-0 border-b border-solid border-gray-200 px-4">
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            role="combobox" aria-expanded="true" aria-controls="crm-palette-list" aria-autocomplete="list"
            aria-activedescendant={flat[active] ? `crm-palette-${flat[active].id}` : undefined}
            placeholder={t("메뉴·리드·기회·회사 검색", "Search menus, leads, opportunities, companies")}
            className="h-12 min-w-0 flex-1 border-0 bg-transparent text-[14px] text-gray-800 outline-none" />
          {loading ? <span className="flex-none text-[12px] text-gray-500">{t("불러오는 중…", "Loading…")}</span> : null}
          <kbd className="flex-none rounded border border-gray-200 px-1.5 font-[inherit] text-[12px] text-gray-500">Esc</kbd>
        </div>
        <div ref={listRef} id="crm-palette-list" role="listbox" className="min-h-0 flex-1 overflow-y-auto py-1.5">
          {groups.length === 0 ? (
            <div className="px-4 py-6 text-center text-[13px] text-gray-500">{loading ? t("불러오는 중…", "Loading…") : t("검색 결과 없음", "No results")}</div>
          ) : groups.map((g) => (
            <div key={g.group} role="group" aria-label={g.title}>
              <div className="px-4 pb-1 pt-2 text-[12px] font-semibold text-gray-500">{g.title}</div>
              {g.items.map((it) => {
                idx += 1;
                const i = idx;
                const on = i === active;
                return (
                  <div key={it.id} id={`crm-palette-${it.id}`} role="option" aria-selected={on} data-palette-idx={i}
                    onMouseMove={() => { if (!on) setActive(i); }}
                    onClick={() => it.run()}
                    className={`mx-1.5 flex h-9 cursor-pointer items-center gap-2 rounded-control px-2.5 text-[13px] ${on ? "bg-primary-subtle text-primary-strong" : "text-gray-800"}`}>
                    <span className="min-w-0 truncate font-medium">{it.label}</span>
                    {it.sub ? <span className="min-w-0 truncate text-[12px] text-gray-500">{it.sub}</span> : null}
                    {on ? <span aria-hidden className="ml-auto flex-none text-[12px] text-gray-500">Enter ↵</span> : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 접힘 상태 1depth 섹션 아이콘 버튼 — 클릭 시 사이드바 펼침 + 해당 섹션 오픈.
function SideSectionIconButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "auto",
        height: 36,
        margin: "2px 8px",
        padding: 0,
        borderRadius: 6,
        border: "none",
        background: active ? "var(--primary-subtle)" : undefined,
        color: active ? "var(--primary)" : "var(--ink-muted)",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 0.12s, color 0.12s",
      }}
      className={active ? undefined : "bg-transparent hover:bg-surface-hover hover:text-ink"}
    >
      <span style={{ display: "flex", color: "currentColor" }}>{icon ?? <FileText size={18} />}</span>
    </button>
  );
}

/** 사이드바에 보이는 메뉴 경로들 — SideMenuItemButton 이 더 긴 형제 메뉴를 알아보는 데 쓴다 */
const MenuPathsCtx = React.createContext<string[]>([]);

function SideMenuItemButton({
  item,
  collapsed,
  onClick,
  indent = false,
}: {
  item: SideMenuItem;
  collapsed: boolean;
  onClick: (item: SideMenuItem, event: React.MouseEvent) => void;
  indent?: boolean;
}) {
  const location = useLocation();
  const activeProjectId = getActiveProjectId(location.pathname, location.search);
  // 이 메뉴 아래 경로인 다른 메뉴가 지금 주소에 맞으면 그쪽만 강조(사이드바 이중 강조 방지) — /crm/partners/:id 처럼 메뉴가 아닌 하위 주소는 그대로 강조
  const menuPaths = React.useContext(MenuPathsCtx);
  const deeper = menuPaths.some((pp) => pp !== item.path && pp.startsWith(`${item.path}/`) && (location.pathname === pp || location.pathname.startsWith(`${pp}/`)));
  const on = (isActive: boolean) => isActive && !deeper;
  // 문서 자동화 페이지 링크는 현재 Opportunity 를 query 로 전달(자동 매핑). 그 외 메뉴는 그대로.
  const to = activeProjectId && item.path.startsWith("/crm/document-automation/")
    ? `${item.path}?project_id=${encodeURIComponent(activeProjectId)}`
    : item.path;
  return (
    <NavLink
      to={to}
      onClick={(event) => onClick(item, event)}
      title={collapsed ? item.label : undefined}
      // Vault UI 메뉴 항목(sidebarShared.sideItemStyle) — hover 면은 클래스로(인라인 배경은 활성일 때만).
      //   펼친 메뉴의 활성 막대는 nav 의 움직이는 막대(navIndicatorStyle)가 맡는다.
      // ★ className 을 함수로 주면 NavLink 가 "active" 를 자동으로 안 붙인다 — 움직이는 막대(a.active 탐색)를 위해 직접 넣는다.
      className={({ isActive }) => (on(isActive) ? "active" : "hover:bg-surface-hover hover:text-ink")}
      style={({ isActive }) => sideItemStyle(on(isActive), collapsed, indent)}
      aria-current={deeper ? false : "page"}
    >
      {({ isActive }) => (
        <>
          <span style={{ flexShrink: 0, display: "flex", color: on(isActive) ? "var(--primary)" : "var(--ink-muted)" }}>{item.icon}</span>
          {!collapsed ? (
            <>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
              {item.status && item.status !== "active" ? <FeatureStatusBadge status={STATUS_BADGE_MAP[item.status]} size="sm" /> : null}
            </>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

function DocEntriesRenderer({
  entries,
  sectionKey,
  isVisible,
  collapsed,
  collapsedGroups,
  onToggleGroup,
  onItemClick,
  language,
}: {
  entries: DocumentMenuEntry[];
  sectionKey: string;
  isVisible: (childKey: string) => boolean;
  collapsed: boolean;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  onItemClick: (item: SideMenuItem, event: React.MouseEvent) => void;
  language: Language;
}) {
  void sectionKey;
  return (
    <>
      {entries.map((entry) => {
        if ("type" in entry && entry.type === "group") {
          // 그룹 내 노출 가능한 자식만 — 전부 숨겨지면 그룹 헤더도 감춘다.
          const visibleChildren = entry.children.filter((child) => isVisible(child.key));
          if (visibleChildren.length === 0) return null;
          const isGroupCollapsed = collapsedGroups.has(entry.key);
          // 아이콘 전용(접힘) 상태: 2depth 자식을 전부 펼치지 않고 그룹을 1depth 아이콘 1개로 대표 표시.
          // 첫 자식 경로로 이동하고 툴팁은 그룹명. 정확한 자식 선택은 호버로 사이드바가 펼쳐질 때 가능.
          if (collapsed) {
            const groupItem: SideMenuItem = {
              key: `document-group-${entry.key}`,
              label: getCrmDocumentLabel(language, entry.key, entry.label),
              icon: docIcon(visibleChildren[0]) ?? <FileText size={14} />,
              path: docEntryPath(visibleChildren[0]),
            };
            return <SideMenuItemButton key={groupItem.key} item={groupItem} collapsed onClick={onItemClick} />;
          }
          return (
            <div key={entry.key}>
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => onToggleGroup(entry.key)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    width: "100%", minHeight: 32, padding: "0 12px 0 14px", background: "transparent",
                    border: "none", cursor: "pointer", color: "var(--crm-text-muted)",
                    fontSize: 12, fontWeight: 400, letterSpacing: 0,
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <FileText size={12} style={{ opacity: 0.6 }} />
                    {getCrmDocumentLabel(language, entry.key, entry.label)}
                  </span>
                  <ChevronDown
                    size={11}
                    style={{ transform: isGroupCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s", flexShrink: 0 }}
                  />
                </button>
              )}
              {!isGroupCollapsed && visibleChildren.map((child) => {
                const item: SideMenuItem = {
                  key: `document-${child.key}`,
                  label: getCrmDocumentLabel(language, child.key, child.label),
                  icon: docIcon(child) ?? <FileText size={14} />,
                  path: docEntryPath(child),
                  status: child.status as SideMenuStatus | undefined,
                };
                return <SideMenuItemButton key={item.key} item={item} collapsed={collapsed} onClick={onItemClick} indent />;
              })}
            </div>
          );
        }
        if (!isVisible(entry.key)) return null;
        const item: SideMenuItem = {
          key: `document-${entry.key}`,
          label: getCrmDocumentLabel(language, entry.key, entry.label),
          icon: docIcon(entry) ?? <FileText size={14} />,
          path: docEntryPath(entry as DocumentMenuItem),
          status: (entry as { status?: SideMenuStatus }).status,
        };
        return <SideMenuItemButton key={item.key} item={item} collapsed={collapsed} onClick={onItemClick} />;
      })}
    </>
  );
}

const layoutStyle: React.CSSProperties = {
  display: "flex",
  // 높이는 .app-layout-shell(CSS)에서 100dvh 폴백과 함께 지정 — 모바일 주소창 대응
  overflow: "hidden",
  background: "var(--crm-bg)",
};

const sidebarStyle: React.CSSProperties = {
  maxWidth: 240,
  transition: "width 0.2s ease, min-width 0.2s ease",
  background: "var(--crm-surface-1)",
  borderRight: "1px solid var(--hairline)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flexShrink: 0,
  position: "relative",
};

const sidebarHeaderStyle: React.CSSProperties = {
  borderBottom: "1px solid var(--hairline)",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
};

const collapseButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--crm-text-muted)",
  width: 32,
  height: 32,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const navStyle: React.CSSProperties = {
  // 활성 막대(absolute)의 기준 — 메뉴 링크의 offsetParent 가 nav 가 된다.
  position: "relative",
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  padding: "8px 0",
};

// Vault UI .v-nav-ind — 위치는 transform 으로만 바꾼다(moveIndicator).
const navIndicatorStyle: React.CSSProperties = {
  position: "absolute",
  left: 0,
  top: 0,
  width: 3,
  height: 20,
  borderRadius: "0 3px 3px 0",
  background: "var(--primary)",
  transformOrigin: "50% 50%",
  pointerEvents: "none",
  opacity: 0,
  zIndex: 1,
};

const sectionButtonStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: 32,
  padding: "0 14px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--crm-text-muted)",
};

// 섹션 제목은 대문자 대신 첫 글자만 대문자(IT-CONSULTING 사이드바와 같은 표기) — docs/ux-laws/JAKOBS_LAW.md 규칙 4
//   Vault UI: 12px/600 회색(ink-muted) — sidebarShared 공용 값.
const sectionLabelStyle = sideSectionLabelStyle;


const mainStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: "var(--crm-surface-1)",
  overflowY: "auto",
  // 모바일에서 A4(210mm)·넓은 표 등 고정폭 콘텐츠가 잘리지 않도록 가로 스크롤 허용
  // (데스크톱은 폭이 충분해 스크롤바가 나타나지 않음)
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  display: "flex",
  flexDirection: "column",
};
